import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Delete, Loader2, Lock } from "lucide-react";
import { api, errMsg, FAMILY_CODE_KEY } from "@/lib/api";
import { afterLoginPath, useAuth } from "@/context/AuthContext";
import { HouseQ, PublicLayout } from "@/components/brand/Brand";
import { KidAvatar } from "@/components/Avatars";

const PINK = "var(--glow-kids)";

function CodeStep({ onFound }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/auth/kid/family/${encodeURIComponent(code.trim())}`);
      onFound(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="kid-code-form">
      <label className="block text-center text-lg font-bold text-q-text" htmlFor="kid-code">What's your family code?</label>
      <input id="kid-code" className="q-input text-center font-display text-3xl font-extrabold uppercase tracking-[0.35em]" maxLength={6}
        value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" autoComplete="off" data-testid="kid-family-code-input" />
      {error && <p className="text-center text-base font-semibold text-red-300" data-testid="kid-code-error">{error}</p>}
      <button className="q-btn min-h-[52px] w-full rounded-xl text-lg text-white" disabled={busy || code.trim().length < 6} data-testid="kid-code-submit-btn"
        style={{ background: `rgb(${PINK})`, boxShadow: `0 0 26px rgba(${PINK},0.4)` }}>
        {busy && <Loader2 className="h-5 w-5 animate-spin" />} Find my family
      </button>
    </form>
  );
}

function AvatarStep({ family, onPick, onReset }) {
  return (
    <div data-testid="kid-avatar-step">
      <p className="text-center text-sm font-semibold uppercase tracking-[0.18em] text-q-muted">{family.hub.name}</p>
      <h2 className="mt-2 text-center font-display text-2xl font-extrabold text-q-text">Who are you?</h2>
      {family.kids.length === 0 ? (
        <p className="mt-8 text-center text-q-muted" data-testid="kid-avatars-empty">No kid profiles yet. Ask a grown-up to add you in Members.</p>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {family.kids.map((k) => (
            <button key={k.id} onClick={() => onPick(k)} data-testid={`kid-avatar-${k.name.toLowerCase()}`}
              className="relative flex flex-col items-center gap-3 rounded-2xl border-2 border-transparent bg-q-raised/70 p-5 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-pink-500/60 hover:shadow-[0_0_28px_rgba(236,72,153,0.25)]">
              <KidAvatar avatar={k.avatar} size={72} />
              <span className="text-lg font-bold text-q-text">{k.name}</span>
              {k.locked && <Lock className="absolute right-3 top-3 h-4 w-4 text-q-faint" />}
            </button>
          ))}
        </div>
      )}
      <button onClick={onReset} className="mx-auto mt-8 block text-sm font-semibold hover:underline" style={{ color: `rgb(${PINK})` }} data-testid="kid-different-code-link">
        Use a different family code
      </button>
    </div>
  );
}

function PinStep({ kid, family, onBack }) {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const press = (d) => {
    setError("");
    setPin((p) => (p.length < 4 ? p + d : p));
  };
  const submit = async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/kid/login", { family_code: family.hub.family_code, kid_id: kid.id, pin });
      localStorage.setItem(FAMILY_CODE_KEY, family.hub.family_code);
      nav(afterLoginPath(signIn(data)), { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="kid-pin-step">
      <button onClick={onBack} className="q-btn q-btn-ghost -ml-2 mb-2 px-2" data-testid="kid-pin-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex flex-col items-center">
        <KidAvatar avatar={kid.avatar} size={80} />
        <h2 className="mt-3 font-display text-2xl font-extrabold text-q-text">Hi {kid.name}! Enter your PIN</h2>
        <input className="q-input mt-5 max-w-[200px] text-center text-2xl tracking-[0.6em]" type="password" inputMode="numeric" maxLength={4}
          value={pin} onChange={(e) => { setError(""); setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); }}
          onKeyDown={(e) => e.key === "Enter" && submit()} aria-label="PIN" data-testid="kid-pin-input" />
        {error && <p className="mt-4 max-w-xs text-center text-base font-semibold text-red-300" data-testid="kid-pin-error">{error}</p>}
        <div className="mt-6 grid w-full max-w-xs grid-cols-3 gap-3">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button key={d} onClick={() => press(d)} data-testid={`kid-pin-key-${d}`}
              className="min-h-[60px] rounded-2xl bg-q-raised font-display text-2xl font-bold text-q-text transition-[background-color,transform] duration-100 hover:bg-q-line active:scale-95">{d}</button>
          ))}
          <button onClick={() => setPin((p) => p.slice(0, -1))} className="flex min-h-[60px] items-center justify-center rounded-2xl text-q-muted hover:bg-q-raised" data-testid="kid-pin-key-del" aria-label="Delete"><Delete className="h-6 w-6" /></button>
          <button onClick={() => press("0")} className="min-h-[60px] rounded-2xl bg-q-raised font-display text-2xl font-bold text-q-text hover:bg-q-line active:scale-95" data-testid="kid-pin-key-0">0</button>
          <button onClick={submit} disabled={busy || pin.length !== 4} data-testid="kid-pin-submit-btn"
            className="min-h-[60px] rounded-2xl font-bold text-white transition-opacity disabled:opacity-40" style={{ background: `rgb(${PINK})` }}>
            {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Go"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KidSignIn() {
  const [family, setFamily] = useState(null);
  const [kid, setKid] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem(FAMILY_CODE_KEY));

  useEffect(() => {
    const saved = localStorage.getItem(FAMILY_CODE_KEY);
    if (!saved) return;
    api.get(`/auth/kid/family/${saved}`).then(({ data }) => setFamily(data))
      .catch(() => localStorage.removeItem(FAMILY_CODE_KEY)).finally(() => setChecking(false));
  }, []);

  const reset = () => {
    localStorage.removeItem(FAMILY_CODE_KEY);
    setFamily(null);
    setKid(null);
  };

  return (
    <PublicLayout>
      <div className="w-full max-w-lg q-rise" data-testid="kid-signin-page">
        <div className="mb-6 flex flex-col items-center">
          <HouseQ size={56} />
          <p className="mt-3 text-xs font-black uppercase tracking-[0.3em]" style={{ color: `rgb(${PINK})` }}>Kids sign in</p>
        </div>
        <div className="rounded-3xl border-2 bg-q-surface p-6 sm:p-8" style={{ borderColor: `rgba(${PINK},0.3)`, boxShadow: `0 0 40px rgba(${PINK},0.15)` }}>
          {checking ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-q-muted" /></div>
          ) : kid ? (
            <PinStep kid={kid} family={family} onBack={() => setKid(null)} />
          ) : family ? (
            <AvatarStep family={family} onPick={setKid} onReset={reset} />
          ) : (
            <CodeStep onFound={(f) => { localStorage.setItem(FAMILY_CODE_KEY, f.hub.family_code); setFamily(f); }} />
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
